import React, { useEffect, useId, useState } from 'react';
import {
  Bell, BellOff, ChevronLeft, ChevronRight, FileText, Link2, MessageSquare,
  PanelRightClose, PanelRightOpen, Users,
} from 'lucide-react';
import { cn } from '../lib/utils';
import EntityActionLog from './EntityActionLog';
import HistoryNotesView from './HistoryNotesView';
import type { ActivityLocale } from '../lib/activityEventTranslator';

export type DocumentChatterTab = 'notes' | 'activity' | 'related';

export interface ChatterFollower {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  isCurrentUser?: boolean;
}

export interface ChatterRelatedDocument {
  id: string;
  label: string;
  type?: string;
  status?: string;
  href?: string;
}

export interface DocumentChatterPanelProps {
  orgId: string;
  entityId: string;
  entityType: string;
  locale?: ActivityLocale;
  className?: string;
  defaultTab?: DocumentChatterTab;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  followers?: ChatterFollower[];
  isFollowing?: boolean;
  onFollowChange?: (following: boolean) => void | Promise<void>;
  relatedDocuments?: ChatterRelatedDocument[];
  onRelatedDocumentOpen?: (document: ChatterRelatedDocument) => void;
}

const TEXT = {
  en: {
    collaboration: 'Document chatter', notes: 'Notes', activity: 'Activity', related: 'Related',
    followers: 'Followers', follow: 'Follow', following: 'Following', noFollowers: 'No followers yet',
    noRelated: 'No related documents', collapse: 'Collapse chatter', expand: 'Expand chatter', open: 'Open document',
  },
  ar: {
    collaboration: 'محادثة المستند', notes: 'الملاحظات', activity: 'النشاط', related: 'المستندات المرتبطة',
    followers: 'المتابعون', follow: 'متابعة', following: 'تتم المتابعة', noFollowers: 'لا يوجد متابعون بعد',
    noRelated: 'لا توجد مستندات مرتبطة', collapse: 'طي المحادثة', expand: 'توسيع المحادثة', open: 'فتح المستند',
  },
} as const;

const TABS: Array<{ id: DocumentChatterTab; icon: typeof MessageSquare }> = [
  { id: 'notes', icon: MessageSquare },
  { id: 'activity', icon: Bell },
  { id: 'related', icon: Link2 },
];

function initials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '?';
}

export default function DocumentChatterPanel({
  orgId,
  entityId,
  entityType,
  locale = 'en',
  className,
  defaultTab = 'notes',
  collapsed: controlledCollapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  followers = [],
  isFollowing = false,
  onFollowChange,
  relatedDocuments = [],
  onRelatedDocumentOpen,
}: DocumentChatterPanelProps) {
  const [tab, setTab] = useState<DocumentChatterTab>(defaultTab);
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [followPending, setFollowPending] = useState(false);
  const [loadedFollowers, setLoadedFollowers] = useState<ChatterFollower[]>([]);
  const [loadedFollowing, setLoadedFollowing] = useState(isFollowing);
  const panelId = useId();
  const text = TEXT[locale];
  const collapsed = controlledCollapsed ?? internalCollapsed;
  // `followers` defaults to a fresh array literal, so never depend on its
  // identity inside effects: doing so re-runs the fetch on every render.
  const hasProvidedFollowers = followers.length > 0;
  const displayedFollowers = hasProvidedFollowers ? followers : loadedFollowers;
  const following = onFollowChange ? isFollowing : loadedFollowing;

  useEffect(() => setTab(defaultTab), [defaultTab, entityId]);
  useEffect(() => {
    setLoadedFollowers([]);
    setLoadedFollowing(isFollowing);
  }, [entityId, entityType, isFollowing]);
  useEffect(() => {
    if (hasProvidedFollowers || typeof window === 'undefined') return;
    const token = localStorage.getItem('app_auth_token');
    if (!token) return;
    const controller = new AbortController();
    const params = new URLSearchParams({ entityType, entityId });
    fetch(`/api/organizations/${orgId}/entity-followers?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(response => response.ok ? response.json() : [])
      .then(rows => {
        if (!Array.isArray(rows)) return;
        setLoadedFollowers(rows);
        setLoadedFollowing(rows.some(follower => follower.isCurrentUser));
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [entityId, entityType, hasProvidedFollowers, orgId]);

  const setCollapsed = (next: boolean) => {
    if (controlledCollapsed === undefined) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  };

  const handleFollow = async () => {
    if (followPending) return;
    setFollowPending(true);
    try {
      const next = !following;
      if (onFollowChange) await onFollowChange(next);
      else {
        const token = localStorage.getItem('app_auth_token');
        if (!token) return;
        const path = next
          ? `/api/organizations/${orgId}/entity-followers`
          : `/api/organizations/${orgId}/entity-followers/${encodeURIComponent(entityType)}/${entityId}`;
        const response = await fetch(path, {
          method: next ? 'POST' : 'DELETE',
          headers: { Authorization: `Bearer ${token}`, ...(next ? { 'Content-Type': 'application/json' } : {}) },
          ...(next ? { body: JSON.stringify({ entityType, entityId }) } : {}),
        });
        if (!response.ok) throw new Error('Could not update follow state');
        setLoadedFollowing(next);
        if (!next) setLoadedFollowers(current => current.filter(follower => !follower.isCurrentUser));
        else {
          const profile = JSON.parse(localStorage.getItem('app_user_profile') || '{}');
          setLoadedFollowers(current => current.some(follower => follower.isCurrentUser) ? current : [...current, { id: 'current-user', name: profile.displayName || profile.email || 'You', email: profile.email, isCurrentUser: true }]);
        }
      }
    } finally {
      setFollowPending(false);
    }
  };

  if (collapsed) {
    return (
      <aside className={cn('flex w-full items-center justify-between rounded-xl border border-border bg-background p-2 shadow-sm lg:w-14 lg:flex-col lg:justify-start lg:gap-2', className)} dir={locale === 'ar' ? 'rtl' : 'ltr'} aria-label={text.collaboration}>
        <button type="button" onClick={() => setCollapsed(false)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={text.expand} title={text.expand}>
          <PanelRightOpen className="h-5 w-5 rtl:scale-x-[-1]" />
        </button>
        <div className="flex gap-1 lg:flex-col">
          {TABS.map(({ id, icon: Icon }) => (
            <button key={id} type="button" onClick={() => { setTab(id); setCollapsed(false); }} className="relative rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={text[id]} title={text[id]}>
              <Icon className="h-4 w-4" />
              {id === 'related' && relatedDocuments.length > 0 && <span className="absolute -end-0.5 -top-0.5 min-w-4 rounded-full bg-primary px-1 text-center text-[9px] leading-4 text-primary-foreground">{relatedDocuments.length}</span>}
            </button>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside className={cn('flex min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm lg:w-[min(100%,24rem)] lg:max-h-[calc(100vh-7rem)]', className)} dir={locale === 'ar' ? 'rtl' : 'ltr'} aria-label={text.collaboration}>
      <header className="border-b border-border p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-foreground">{text.collaboration}</p>
            <p className="truncate text-[10px] text-muted-foreground">{entityType}</p>
          </div>
          <button type="button" onClick={() => setCollapsed(true)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-controls={panelId} aria-label={text.collapse} title={text.collapse}>
            <PanelRightClose className="h-5 w-5 rtl:scale-x-[-1]" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-muted/60 p-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground"><Users className="h-3.5 w-3.5" />{text.followers}</div>
            {displayedFollowers.length > 0 ? (
              <div className="mt-1 flex -space-x-1.5 rtl:space-x-reverse">
                {displayedFollowers.slice(0, 5).map(follower => follower.avatarUrl ? (
                  <img key={follower.id} src={follower.avatarUrl} alt={follower.name} title={follower.name} className="h-7 w-7 rounded-full border-2 border-background object-cover" />
                ) : (
                  <span key={follower.id} title={follower.name} className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-primary text-[9px] font-bold text-primary-foreground">{initials(follower.name)}</span>
                ))}
                {displayedFollowers.length > 5 && <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-muted text-[9px] font-bold">+{displayedFollowers.length - 5}</span>}
              </div>
            ) : <p className="mt-1 text-[10px] text-muted-foreground">{text.noFollowers}</p>}
          </div>
          <button type="button" onClick={() => void handleFollow()} disabled={followPending} className={cn('flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-50', following ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted')}>
            {following ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}{following ? text.following : text.follow}
          </button>
        </div>
      </header>

      <div role="tablist" aria-label={text.collaboration} className="grid grid-cols-3 border-b border-border bg-muted/30 p-1">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} aria-controls={`${panelId}-${id}`} onClick={() => setTab(id)} className={cn('flex min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-semibold transition', tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            <Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{text[id]}</span>{id === 'related' && relatedDocuments.length > 0 && <span className="rounded-full bg-primary/10 px-1.5 text-[9px] text-primary">{relatedDocuments.length}</span>}
          </button>
        ))}
      </div>

      <div id={panelId} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4">
        {tab === 'notes' && <div id={`${panelId}-notes`} role="tabpanel"><HistoryNotesView orgId={orgId} entityId={entityId} entityType={entityType} showActionLog={false} showHeader={false} locale={locale} compact /></div>}
        {tab === 'activity' && <div id={`${panelId}-activity`} role="tabpanel"><EntityActionLog orgId={orgId} entityId={entityId} entityType={entityType} locale={locale} compact /></div>}
        {tab === 'related' && (
          <div id={`${panelId}-related`} role="tabpanel" className="space-y-2">
            {relatedDocuments.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground"><FileText className="mx-auto mb-2 h-6 w-6" />{text.noRelated}</div>
            ) : relatedDocuments.map(document => {
              const content = <><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted"><FileText className="h-4 w-4" /></span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{document.label}</span><span className="block truncate text-[10px] text-muted-foreground">{[document.type, document.status].filter(Boolean).join(' • ')}</span></span>{locale === 'ar' ? <ChevronLeft className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</>;
              const itemClass = 'flex w-full items-center gap-3 rounded-xl border border-border bg-background p-3 text-start transition hover:bg-muted/50';
              if (onRelatedDocumentOpen) return <button key={document.id} type="button" onClick={() => onRelatedDocumentOpen(document)} className={itemClass} aria-label={`${text.open}: ${document.label}`}>{content}</button>;
              if (document.href) return <a key={document.id} href={document.href} className={itemClass} aria-label={`${text.open}: ${document.label}`}>{content}</a>;
              return <div key={document.id} className={itemClass}>{content}</div>;
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
